import type { DownResponseData } from '@/common/interop/down-messages';
import type {
  AppIdentifier,
  AppIntent,
  AppMetadata,
  Channel,
  Context,
  ContextHandler,
  DesktopAgent,
  ImplementationMetadata,
  IntentResolution,
  Listener,
  PrivateChannel,
} from '@finos/fdc3';
import type { AgentWebSocket } from './websocket';

import { ConnectifiChannel } from './channel';
import { SubscriptionManager } from './subscription';

const hasBridgeChannel = async (channel: string): Promise<boolean> => {
  const bridgeChannels = await window.fdc3?.getUserChannels();
  return bridgeChannels?.some((c) => c.id === channel);
};

const createIntentResolution = (
  resolution: DownResponseData['raiseIntent'],
): IntentResolution => {
  return {
    source: resolution.source,
    version: resolution.version,
    intent: resolution.intent,
    getResult: async () => resolution.result!,
  };
};

export class ConnectifiFDC3Api implements DesktopAgent {
  constructor(
    private ws: Pick<AgentWebSocket, 'action' | 'request'>,
    private subManager: SubscriptionManager,
    private bridge: boolean,
  ) {}

  open(
    target: AppIdentifier | string,
    context?: Context,
  ): Promise<AppIdentifier> {
    return this.ws.request('open', {
      target: target,
      context: context,
    });
  }

  async broadcast(context: Context): Promise<void> {
    const broadcastPromise = this.ws.request('broadcast', {
      context: context,
    });

    if (this.bridge) {
      await window.fdc3?.broadcast(context);
    }

    await broadcastPromise;
  }

  async raiseIntent(
    intent: string,
    context: Context,
    app?: AppIdentifier | string | undefined,
  ): Promise<IntentResolution> {
    const resolution = await this.ws.request('raiseIntent', {
      intent: intent,
      target: app,
      context: context,
    });
    return createIntentResolution(resolution);
  }

  async raiseIntentForContext(
    context: Context,
    app?: AppIdentifier | string | undefined,
  ): Promise<IntentResolution> {
    const resolution = await this.ws.request('raiseIntentForContext', {
      target: app,
      context: context,
    });
    return createIntentResolution(resolution);
  }

  addContextListener(handler: ContextHandler): Promise<Listener>;

  addContextListener(
    contextType: string | null,
    handler: ContextHandler,
  ): Promise<Listener>;

  async addContextListener(
    contextTypeOrHandler: string | null | ContextHandler,
    handlerSecondArg?: ContextHandler,
  ): Promise<Listener> {
    const handler: ContextHandler =
      typeof contextTypeOrHandler === 'function'
        ? contextTypeOrHandler
        : handlerSecondArg!;
    const contextType: string =
      typeof contextTypeOrHandler === 'string' ? contextTypeOrHandler : '*';

    await this.ws.request('addContextListener', {
      contextType: contextType,
    });

    return this.subManager.createContextListener(
      this.ws,
      contextType,
      undefined,
      handler,
    );
  }

  async addIntentListener(
    intent: string,
    handler: ContextHandler,
  ): Promise<Listener> {
    await this.ws.request('addIntentListener', {
      intent: intent,
    });

    return this.subManager.createIntentListener(this.ws, intent, handler);
  }

  async findIntent(intent: string, context: Context): Promise<AppIntent> {
    return this.ws.request('findIntent', {
      intent: intent,
      context: context,
    });
  }

  findIntentsByContext(context: Context): Promise<AppIntent[]> {
    return this.ws.request('findIntentsByContext', {
      context: context,
    });
  }

  // Left here to satisfy Desktop Agent interface. Not used anywhere
  async getSystemChannels(): Promise<Channel[]> {
    return this.getUserChannels();
  }

  async getUserChannels(): Promise<Channel[]> {
    const channels = await this.ws.request('getUserChannels', {});
    return channels.map(
      (c) =>
        new ConnectifiChannel(
          this.ws,
          this.subManager,
          c.id,
          'user',
          c.displayMetadata,
        ),
    );
  }

  async getOrCreateChannel(channelId: string): Promise<Channel> {
    const channel = await this.ws.request('getOrCreateChannel', {
      channelId,
    });
    return new ConnectifiChannel(
      this.ws,
      this.subManager,
      channel.id,
      // TODO: See what really should be done about system channels
      channel.type as Channel['type'],
      channel.displayMetadata,
    );
  }

  async joinChannel(channel: string): Promise<void> {
    const joinChannelPromise = this.ws.request('joinChannel', { channel });

    if (this.bridge) {
      const isBridgeChannel = await hasBridgeChannel(channel);
      if (isBridgeChannel) {
        if (window.fdc3.joinUserChannel) {
          window.fdc3.joinUserChannel(channel);
        } else if (window.fdc3.joinChannel) {
          window.fdc3.joinChannel(channel);
        }
      }
    }

    await joinChannelPromise;
  }

  async joinUserChannel(channel: string): Promise<void> {
    const joinChannelPromise = this.ws.request('joinChannel', { channel });

    if (this.bridge) {
      const isBridgeChannel = await hasBridgeChannel(channel);
      if (isBridgeChannel) {
        if (window.fdc3.joinUserChannel) {
          window.fdc3.joinUserChannel(channel);
        } else if (window.fdc3.joinChannel) {
          window.fdc3.joinChannel(channel);
        }
      }
    }

    await joinChannelPromise;
  }

  async leaveCurrentChannel(): Promise<void> {
    const leaveChannelPromise = this.ws.request('leaveCurrentChannel', {});

    if (this.bridge) {
      const channel = await this.getCurrentChannel();
      if (channel) {
        const isBridgeChannel = await hasBridgeChannel(channel.id);
        if (isBridgeChannel) {
          window.fdc3.leaveCurrentChannel();
        }
      }
    }

    await leaveChannelPromise;
  }

  async getCurrentChannel(): Promise<Channel | null> {
    const channel = await this.ws.request('getCurrentChannel', {});
    if (!channel) {
      return null;
    }

    return new ConnectifiChannel(
      this.ws,
      this.subManager,
      channel.id,
      channel.type as Channel['type'],
      channel.displayMetadata,
    );
  }

  getInfo(): Promise<ImplementationMetadata> {
    return this.ws.request('getInfo', {});
  }

  findInstances(app: AppIdentifier): Promise<AppIdentifier[]> {
    return this.ws.request('findInstances', app);
  }

  getAppMetadata(app: AppIdentifier): Promise<AppMetadata> {
    return this.ws.request('getAppMetadata', app);
  }

  createPrivateChannel(): Promise<PrivateChannel> {
    return this.ws.request('createPrivateChannel', {});
  }
}
